import { type ViewKey } from './viewState';

/**
 * The part of the shell's state that belongs in its address.
 *
 * The shell is one route, so which view is open, which thread is being read and which Lens is on
 * screen were all plain component state. Nothing about it was bookmarkable or shareable, while
 * both surfaces it composes are: a chat session has `/ask-richard/shared/:sessionId` and a Lens
 * has `/le-lens/:lensId`. Sending someone "look at this" meant sending them the default view.
 *
 * A card selection carries a title as well as an id, and only the id is safe to put in a link:
 * a title written into the address would go stale the moment the card was renamed. The shell
 * therefore restores the id and looks the title up in the board the card belongs to, holding the
 * composer subject back until it resolves and dropping the selection if the card has since gone.
 */
export const SHELL_VIEW_PARAM = 'view';
export const SHELL_THREAD_PARAM = 'thread';
export const SHELL_LENS_PARAM = 'lens';
export const SHELL_CARD_PARAM = 'card';
export const SHELL_EXPORT_PARAM = 'export';
/** The governed dataset open in the Data catalogue. Only meaningful on the catalogue view. */
export const SHELL_DATASET_PARAM = 'dataset';
export const SHELL_DOMAIN_PARAM = 'domain';
export const SHELL_METRIC_PARAM = 'metric';
export const SHELL_CATALOGUE_SECTION_PARAM = 'section';

/**
 * Short-lived presentation switches used while the Analytics landing was being chosen.
 * They are not product state and must not survive in shared or bookmarked links now that
 * there is one canonical design.
 */
const RETIRED_PRESENTATION_PARAMS = ['palette'] as const;
const RETIRED_DOMAIN_IDS: Readonly<Record<string, string>> = {
  communications: 'growth',
  demand: 'growth',
  marketing: 'growth',
  membership: 'loyalty',
};

export type CatalogueSection = 'home' | 'data';

/**
 * The catalogue view carries a second, larger slice of address params that this module
 * deliberately does not know about: the Commercial page's own period/compare/scope/trend state
 * (`period`, `compare`, `from`, `to`, `cfrom`, `cto`, `product`, `market`, `channel`, `focus`,
 * `split`, `grain`, `chart`, `bview`, `cols`), owned end to end by
 * `src/pages/LeLens/catalogue/commercial/analyticsState.ts`
 * (`COMMERCIAL_VIEW_PARAM_KEYS`, `encodeState`/`decodeState`).
 *
 * Precedence: `decodeShellView` only ever reads the `SHELL_*` keys above; every other param in
 * the query string, page-owned ones included, passes through untouched because neither function
 * enumerates the full param set. `applyShellView` mirrors that: it builds on a copy of the
 * caller's current `URLSearchParams` and only sets/deletes the `SHELL_*` keys via
 * {@link shellViewParams}, so a page's own params survive both a dataset/domain/metric
 * precedence change (see the mutual-exclusivity rule below) and non-home view changes (e.g.
 * leaving `catalogue` for `thread`). Returning to the askRichard home is the deliberate reset:
 * it clears the query string so an old analytics filter cannot linger behind the landing page.
 * If a future page-owned key collides with a `SHELL_*` name, that key must be renamed on the
 * page's side; the shell's key list is the fixed one.
 */

/** The shell opens on the ask-first workspace landing. */
export const DEFAULT_VIEW: ViewKey = 'landing';

const VIEWS: ReadonlyArray<ViewKey> = [
  'landing',
  'thread',
  'lens',
  'lensIndex',
  'catalogue',
  'dataGuide',
  'certification',
];

export type ShellUrlState = {
  view: ViewKey;
  threadId: string | null;
  lensId: string | null;
  /** The card the composer is scoped to. Only meaningful alongside the Lens that holds it. */
  cardId: string | null;
  /** The governed dataset open in the Data catalogue. Only carried on the catalogue view. */
  datasetId?: string | null;
  /** Curated analytics domain open inside the Data catalogue. */
  domainId?: string | null;
  /** Metric detail open inside a curated domain. */
  metricId?: string | null;
  /** Analytics defaults to Home; `data` opens the supporting governed-data library. */
  catalogueSection?: CatalogueSection;
};

/**
 * Reads the shell's state out of a query string, tolerantly. A link can be truncated by a chat
 * client or hand-edited, and neither should be an error page: an unreadable value means the
 * default. An unknown view name in particular must not leave the shell rendering nothing.
 */
export function decodeShellView(params: URLSearchParams): ShellUrlState {
  // Only the SHELL_* keys are read here. Any other param on `params` (notably the Commercial
  // page's own period/compare/scope/trend keys) is left in `params` for the page itself to read
  // via `analyticsState.ts`'s `decodeState` and is never inspected or validated by this function.
  const rawView = params.get(SHELL_VIEW_PARAM);
  const threadId = params.get(SHELL_THREAD_PARAM)?.trim() || null;
  const lensId = params.get(SHELL_LENS_PARAM)?.trim() || null;
  // Older in-app Lens links carried only `?lens=<id>`. Treat that as a Lens destination
  // rather than silently dropping a signed-in reader into the blank conversation view.
  const view = VIEWS.find((candidate) => candidate === rawView) ?? (lensId ? 'lens' : DEFAULT_VIEW);
  // A card without its board cannot be shown, so it is not carried on its own.
  const cardId = (lensId && params.get(SHELL_CARD_PARAM)?.trim()) || null;
  if (view === 'catalogue') {
    const catalogueSection: CatalogueSection = params.get(SHELL_CATALOGUE_SECTION_PARAM) === 'data' ? 'data' : 'home';
    const datasetId = params.get(SHELL_DATASET_PARAM)?.trim() || null;
    const requestedDomainId = params.get(SHELL_DOMAIN_PARAM)?.trim() || null;
    const domainId =
      datasetId || catalogueSection === 'data'
        ? null
        : requestedDomainId
          ? (RETIRED_DOMAIN_IDS[requestedDomainId] ?? requestedDomainId)
          : null;
    const metricId = domainId ? params.get(SHELL_METRIC_PARAM)?.trim() || null : null;
    return { view, threadId, lensId, cardId, datasetId, domainId, metricId, catalogueSection };
  }
  return { view, threadId, lensId, cardId };
}

/**
 * The params the shell should carry, as entries to apply over whatever else is in the address.
 * A null value means remove the key, so a board back at its default keeps a clean URL rather
 * than advertising `?view=thread`.
 *
 * A lens id is only meaningful while a Lens is what is on screen, and a thread id only while a
 * thread is, so each is dropped when its own view is not the one open. Otherwise a link would
 * carry a stale id that the receiving shell would then try to restore.
 */
export function shellViewParams(state: ShellUrlState): Array<[string, string | null]> {
  const onLens = state.view === 'lens';
  const onThread = state.view === 'thread';
  return [
    [SHELL_VIEW_PARAM, state.view === DEFAULT_VIEW ? null : state.view],
    [SHELL_THREAD_PARAM, onThread ? state.threadId : null],
    [SHELL_LENS_PARAM, onLens ? state.lensId : null],
    [SHELL_CARD_PARAM, onLens && state.lensId ? state.cardId : null],
    [SHELL_DATASET_PARAM, state.view === 'catalogue' ? (state.datasetId ?? null) : null],
    [SHELL_CATALOGUE_SECTION_PARAM, state.view === 'catalogue' && state.catalogueSection === 'data' ? 'data' : null],
    [
      SHELL_DOMAIN_PARAM,
      state.view === 'catalogue' && state.catalogueSection !== 'data' && !state.datasetId
        ? (state.domainId ?? null)
        : null,
    ],
    [
      SHELL_METRIC_PARAM,
      state.view === 'catalogue' && state.catalogueSection !== 'data' && state.domainId && !state.datasetId
        ? (state.metricId ?? null)
        : null,
    ],
  ];
}

/**
 * Applies {@link shellViewParams} to a copy of `current`, leaving every other param untouched
 * unless the destination is the askRichard home.
 * That includes the Commercial page's own params (see the block comment above `SHELL_DATASET_PARAM`):
 * this function only ever sets or deletes the fixed `SHELL_*` keys, so a `period=month_to_date`
 * already on `current` rides along unchanged through a dataset-open, a domain-switch, or a
 * navigation away from `catalogue` entirely.
 */
export function applyShellView(current: URLSearchParams, state: ShellUrlState): URLSearchParams {
  // Home is a fresh starting point, not another analytics destination. Clearing the whole query
  // string prevents hidden Product/Market/etc. state from surviving after the page is gone.
  const next = state.view === DEFAULT_VIEW ? new URLSearchParams() : new URLSearchParams(current);
  for (const key of RETIRED_PRESENTATION_PARAMS) {
    next.delete(key);
  }
  if (state.view !== 'lens' || current.get(SHELL_LENS_PARAM) !== state.lensId) {
    next.delete('lensView');
  }
  for (const [key, value] of shellViewParams(state)) {
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
  }
  return next;
}

/**
 * True when the shell is showing the `'lens'` view with no real Lens id behind it: exactly the
 * state a `?view=lens&create=1`/`?view=lens&plan=<planId>` URL door's own `view=lens` leaves
 * `decodeShellView` in (those params name no `lens` id; `startLensConversation` opens a sheet on
 * top instead of a real Lens view). Left alone whenever a genuine Lens is open (`activeLensId`
 * set): a "New Lens" click from inside a real Lens must not disturb it.
 */
export function shouldResetBrokenLensView(view: ViewKey, activeLensId: string | null): boolean {
  return view === 'lens' && !activeLensId;
}

export function unifiedAskRichardPath(legacySearch: string): string {
  const legacy = new URLSearchParams(legacySearch);
  const sessionId = legacy.get('sessionId');
  if (!sessionId) {
    return '/ask-richard-unified';
  }
  const next = new URLSearchParams([
    [SHELL_VIEW_PARAM, 'thread'],
    [SHELL_THREAD_PARAM, sessionId],
  ]);
  if (legacy.get(SHELL_EXPORT_PARAM) === 'pdf') {
    next.set(SHELL_EXPORT_PARAM, 'pdf');
  }
  return `/ask-richard-unified?${next.toString()}`;
}
